import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  RawAxiosRequestHeaders,
} from 'axios';
import { TOKEN } from 'features/auth/constants';
import Cookies from 'js-cookie';
import { KEYCLOAK_CONFIG, KeycloakRequestBaseURL } from 'lib/keycloak/keycloak';
import { TRetrievedKeycloakTokenResponse } from 'lib/keycloak/types';
import qs from 'qs';
import { getPath } from 'routes/router-paths';

/** Set application auth token
 * @param tokenType
 * @param token
 * @param expiration chỉ truyền vào số giây
 * */
export const setAuthToken = (
  tokenType: TOKEN,
  token: string,
  expiration?: number
) => {
  Cookies.set(tokenType, token, {
    expires: expiration ? (expiration - 60) / 3600 / 24 : 0,
  });
};

/** Get auth token with provided token type
 * App will decode token to get exp and compare with the current time
 * If expired, remove this token and return null
 * If token invalid, remove token and return null
 * If valid, return the token
 * */
export const getAuthToken = (tokenType: TOKEN): string | null => {
  const token = Cookies.get(tokenType);
  if (!token) return null;
  return token;
};

export const clearAuthToken = () => {
  Cookies.remove(TOKEN.ACCESS_TOKEN);
  Cookies.remove(TOKEN.REFRESH_TOKEN);
  Cookies.remove(TOKEN.KC_TOKEN);
};

let isAlreadyFetchingAccessToken = false;
// eslint-disable-next-line @typescript-eslint/ban-types
let subscribers: Function[] = [];

const onAccessTokenFetched = (token: string) => {
  subscribers = subscribers.filter(callback => callback(token));
};

// eslint-disable-next-line @typescript-eslint/ban-types
const addSubscriber = (callback: Function) => {
  subscribers.push(callback);
};

export const handleRefreshToken = async (
  originalRequest: AxiosRequestConfig
) => {
  const retryOriginalRequest: Promise<AxiosResponse> = new Promise(resolve => {
    addSubscriber((token: string) => {
      (originalRequest.headers as RawAxiosRequestHeaders).Authorization =
        'Bearer ' + token;
      return resolve(axios(originalRequest));
    });
  });
  const refresh_token = getAuthToken(TOKEN.REFRESH_TOKEN);
  if (!refresh_token) {
    clearAuthToken();
    window.location.href = getPath(
      'loginPage',
      qs.stringify({
        redirectedFrom: window.location.pathname,
      })
    );
    return;
  }

  if (!isAlreadyFetchingAccessToken && refresh_token) {
    isAlreadyFetchingAccessToken = true;

    const params = {
      grant_type: 'refresh_token',
      client_id: KEYCLOAK_CONFIG.clientId,
      client_secret: KEYCLOAK_CONFIG.clientSecret,
      refresh_token,
    };

    try {
      const response = await axios.request<
        unknown,
        TRetrievedKeycloakTokenResponse
      >({
        url: `${KeycloakRequestBaseURL}token`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: params,
      });

      isAlreadyFetchingAccessToken = false;

      const {
        access_token,
        refresh_token,
        expires_in,
        refresh_expires_in,
        id_token,
      } = response;

      setAuthToken(TOKEN.ACCESS_TOKEN, access_token, expires_in);
      setAuthToken(TOKEN.REFRESH_TOKEN, refresh_token, refresh_expires_in);
      setAuthToken(TOKEN.KC_TOKEN, id_token, refresh_expires_in);
      onAccessTokenFetched(access_token);
    } catch (error) {
      isAlreadyFetchingAccessToken = false;
      clearAuthToken();
      window.location.href = getPath(
        'loginPage',
        qs.stringify({
          redirectedFrom: window.location.pathname,
        })
      );
      throw error;
    }
  }

  return retryOriginalRequest;
};
